// SinglePharmacy.js
// -----------------------------------------------------------------------------
// A Head-Office-style view scoped to ONE pharmacy. A dropdown at the top selects
// the pharmacy (default: first member). Each module is a full-width card: the
// same label as the Head Office summary card, then the same detail grid you get
// by drilling into Head Office — but for this single pharmacy. Clicking a row
// opens the history popup (fridge / cleaning reuse PopupContent).
// -----------------------------------------------------------------------------
import React, { useEffect, useState, useCallback, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import PopupContent from "./PopupContent";
import { useMaskNames, displayName, MaskToggleButton } from "./nameMask";
import "./Popup.css";

const API_BASE = "https://api.pharmacegroup.co.uk";

// Module cards shown on the page, in order. label = same as Head Office.
const CARDS = [
  { key: "fridge",        label: "Fridge monitoring" },
  { key: "cleaning",      label: "Cleaning" },
  { key: "dateChecking",  label: "Date checking" },
  { key: "handover",      label: "Handover diary" },
  { key: "cdRegister",    label: "CD register" },
  { key: "patientSafety", label: "Patient safety" },
  { key: "accidentBook",  label: "Accident book" },
  { key: "visitorLogs",   label: "Visitor logs" }
];

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return String(d);
  return dt.toLocaleDateString("en-GB");
};
const fmtDateTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return String(d);
  return dt.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

async function apiGet(path, token) {
  const res = await fetch(`${API_BASE}/api/headoffice/${path}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function SinglePharmacy({ token, goTo }) {
  const masked = useMaskNames();
  const viewerOrg = localStorage.getItem("organisationName") || "";
  const isAdmin = viewerOrg === "Pharmace all";
  const orgQS = (isAdmin || !viewerOrg) ? "" : `?organisationName=${encodeURIComponent(viewerOrg)}`;

  const [pharmacies, setPharmacies] = useState([]);
  const [selected, setSelected] = useState(null); // { pharmacyId, pharmacyName }
  const [loadingList, setLoadingList] = useState(true);

  // Fridge/cleaning history popup (reuses PopupContent shell).
  const [popupData, setPopupData] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);
  // Generic row-detail popup for the other modules.
  const [rowDetail, setRowDetail] = useState(null); // { title, fields: [[label,value],...] }

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    apiGet(`single/pharmacies${orgQS}`, token)
      .then(list => {
        if (cancelled) return;
        setPharmacies(list || []);
        // Default to first pharmacy in the (alphabetical) members list.
        if (list && list.length) setSelected(list[0]);
        setLoadingList(false);
      })
      .catch(() => { if (!cancelled) setLoadingList(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Open the fridge/cleaning history popup for the selected pharmacy, using the
  // same per-pharmacy endpoints Head Office uses (POST {pharmacyName}).
  const openHistory = useCallback(async (moduleKey, preloadedItems) => {
    if (!selected) return;

    // Cleaning: the card already holds exactly what the popup shows (area +
    // next due). Build it directly — no name-matching, no extra fetch.
    if (moduleKey === "cleaning") {
      const details = (preloadedItems || []).map(a => ({
        areaName: a.areaName,
        nextDue: a.nextDue ? new Date(a.nextDue).toLocaleDateString("en-GB") : "—"
      }));
      setPopupData({
        column: "Cleaning",
        pharmacyName: selected.pharmacyName,
        cleaningDetails: details,
        isCleaningPopup: true,
        onClose: () => setPopupData(null)
      });
      return;
    }

    // Fridge: needs full log history (not on the card), so fetch by name.
    setPopupLoading(true);
    try {
      const res = await fetch(`${API_BASE}/fridgelogs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pharmacyName: selected.lookupName || selected.pharmacyName })
      });
      const json = await res.json();
      setPopupData({
        column: "Fridge",
        pharmacyName: selected.pharmacyName,
        fridgeLogs: json.logs || [],
        isFridgePopup: true,
        onClose: () => setPopupData(null)
      });
    } catch (_) {
      setPopupData(null);
    } finally {
      setPopupLoading(false);
    }
  }, [selected, token]);

  return (
    <div style={S.page}>
      <div style={S.headerRow}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>Single pharmacy</h2>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
            Compliance overview for one pharmacy.
          </div>
        </div>
        <MaskToggleButton />
      </div>

      {/* Pharmacy selector */}
      <div style={S.selectorRow}>
        <label style={{ fontSize: 13, fontWeight: 600, marginRight: 8 }}>Pharmacy:</label>
        {loadingList ? (
          <span style={{ color: "#6b7280", fontSize: 13 }}>Loading…</span>
        ) : pharmacies.length === 0 ? (
          <span style={{ color: "#6b7280", fontSize: 13 }}>No pharmacies available.</span>
        ) : (
          <select
            value={selected?.pharmacyId || ""}
            onChange={(e) => {
              const p = pharmacies.find(x => String(x.pharmacyId) === e.target.value);
              setSelected(p || null);
            }}
            style={S.select}
          >
            {pharmacies.map((p, i) => (
              <option key={p.pharmacyId || `np-${i}`} value={p.pharmacyId || ""} disabled={!p.pharmacyId}>
                {displayName(p.pharmacyName, masked)}{!p.pharmacyId ? " (unavailable)" : ""}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Module cards */}
      {selected && selected.pharmacyId && CARDS.map(card => (
        <ModuleCard
          key={card.key}
          card={card}
          pharmacy={selected}
          token={token}
          masked={masked}
          onOpenHistory={openHistory}
          onRowDetail={setRowDetail}
        />
      ))}

      {/* Periodic tasks — daily / weekly / monthly / yearly, inline auto-save */}
      {selected && selected.pharmacyId && (
        <PeriodicTasksCard pharmacy={selected} token={token} />
      )}

      {selected && !selected.pharmacyId && (
        <div style={S.card}>
          <div style={{ color: "#7f1d1d" }}>
            This pharmacy can’t be opened yet — its tenant database mapping is missing.
          </div>
        </div>
      )}

      {/* Activity cards — clinic (services), NMS and routes, focused on this pharmacy. */}
      {selected && (
        <>
          <ActivityCard
            label="Clinic activity"
            endpoint="services_analysis"
            listKey="services"
            token={token}
            pharmacyName={selected.pharmacyName}
            kind="services"
          />
          <ActivityCard
            label="NMS activity"
            endpoint="nms_analysis"
            listKey="nms"
            token={token}
            pharmacyName={selected.pharmacyName}
            kind="nms"
          />
          <RouteActivityCard
            token={token}
            pharmacyName={selected.pharmacyName}
            goTo={goTo}
          />
        </>
      )}

      {/* History popup (fridge / cleaning) */}
      {popupLoading && (
        <div className="popup"><div className="popup-content">Loading…</div></div>
      )}
      {popupData && (
        <div className="popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setPopupData(null)}>X</button>
            <PopupContent popupData={popupData} />
          </div>
        </div>
      )}

      {/* Generic row-detail popup (handover, CD, patient safety, accident, visitors) */}
      {rowDetail && (
        <div className="popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setRowDetail(null)}>X</button>
            <h3 style={{ marginTop: 0 }}>{rowDetail.title}</h3>
            <table style={S.table}>
              <tbody>
                {rowDetail.fields.map(([k, v], i) => (
                  <tr key={i}>
                    <td style={{ ...S.td, fontWeight: 600, width: 180, verticalAlign: "top" }}>{k}</td>
                    <td style={S.td}>{v == null || v === "" ? "—" : String(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// One full-width module card: label + that pharmacy's detail grid.
// -----------------------------------------------------------------------------
function ModuleCard({ card, pharmacy, token, masked, onOpenHistory, onRowDetail }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setData(null);
    apiGet(`single/${card.key}?pharmacyId=${encodeURIComponent(pharmacy.pharmacyId)}`, token)
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [card.key, pharmacy.pharmacyId, token]);

  return (
    <div style={S.card}>
      <div style={S.cardLabel}>{card.label}</div>
      {loading ? (
        <div style={S.muted}>Loading…</div>
      ) : error ? (
        <div style={{ color: "#7f1d1d", fontSize: 13 }}>Couldn’t load: {error}</div>
      ) : (
        <CardGrid card={card} data={data} masked={masked} onOpenHistory={onOpenHistory} onRowDetail={onRowDetail} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Per-module grid renderers.
// -----------------------------------------------------------------------------
function CardGrid({ card, data, onOpenHistory, onRowDetail }) {
  if (!data) return <div style={S.muted}>No data.</div>;

  switch (card.key) {
    case "fridge": {
      const items = data.items || [];
      if (!items.length) return <div style={S.muted}>No fridges recorded.</div>;
      return (
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Fridge</th><th style={S.th}>Last reading</th>
            <th style={S.th}>Min °C</th><th style={S.th}>Max °C</th><th style={S.th}>Status</th>
          </tr></thead>
          <tbody>
            {items.map((f, i) => {
              // red if out of range / recheck overdue; amber if not logged today.
              const bad = f.outOfRange || f.recheckOverdue;
              const stale = !bad && (!f.loggedToday || !f.hasReading);
              const bg = bad ? "#fee2e2" : stale ? "#fef3c7" : "transparent";
              const statusText = bad ? (f.outOfRange ? "Out of range" : "Recheck overdue")
                : stale ? "Not logged today" : "OK";
              return (
                <tr key={i} style={{ background: bg, cursor: "pointer" }} onClick={() => onOpenHistory("fridge")}>
                  <td style={S.td}>{f.fridgeName}</td>
                  <td style={S.td}>{fmtDateTime(f.lastReadingAt)}</td>
                  <td style={S.td}>{f.minTemperature ?? "—"}</td>
                  <td style={S.td}>{f.maxTemperature ?? "—"}</td>
                  <td style={S.td}>{statusText}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    case "cleaning":
    case "dateChecking": {
      const items = data.items || [];
      if (!items.length) return <div style={S.muted}>No areas recorded.</div>;
      const isCleaning = card.key === "cleaning";
      return (
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Area</th><th style={S.th}>Next due</th><th style={S.th}>Status</th>
          </tr></thead>
          <tbody>
            {items.map((a, i) => {
              const bg = a.overdue ? "#fee2e2" : a.neverLogged ? "#fef3c7" : "transparent";
              const statusText = a.overdue ? "Overdue" : a.neverLogged ? "Never logged" : "Up to date";
              return (
                <tr key={i}
                    style={{ background: bg, cursor: "pointer" }}
                    onClick={isCleaning
                      ? () => onOpenHistory("cleaning", items)
                      : () => onRowDetail({
                          title: `Date checking — ${a.areaName}`,
                          fields: [
                            ["Area", a.areaName], ["Next due", fmtDate(a.nextDue)],
                            ["Frequency", a.frequency], ["Status", statusText]
                          ]
                        })}>
                  <td style={S.td}>{a.areaName}</td>
                  <td style={S.td}>{fmtDate(a.nextDue)}</td>
                  <td style={S.td}>{statusText}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    case "handover": {
      const groups = [
        { key: "overdue", title: "Overdue", bg: "#fee2e2" },
        { key: "pending", title: "Pending", bg: "#fef3c7" },
        { key: "completedToday", title: "Completed today", bg: "#dcfce7" }
      ];
      const any = groups.some(g => (data[g.key] || []).length);
      if (!any) return <div style={S.muted}>Nothing outstanding.</div>;
      return (
        <>
          {groups.map(g => {
            const items = data[g.key] || [];
            if (!items.length) return null;
            return (
              <div key={g.key} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{g.title} ({items.length})</div>
                <table style={S.table}>
                  <thead><tr>
                    <th style={S.th}>Topic</th><th style={S.th}>For</th>
                    <th style={S.th}>{g.key === "completedToday" ? "Completed" : "Due"}</th>
                  </tr></thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i} style={{ background: g.bg, cursor: "pointer" }}
                          onClick={() => onRowDetail({
                            title: `Handover — ${it.topic || "item"}`,
                            fields: [
                              ["Topic", it.topic], ["For", it.fao || it.patientName],
                              ["Description", it.description], ["Due", fmtDate(it.dueDate)],
                              ["Completed", it.completedAt ? fmtDateTime(it.completedAt) : null],
                              ["Days overdue", it.daysOverdue]
                            ]
                          })}>
                        <td style={S.td}>{it.topic || "—"}</td>
                        <td style={S.td}>{it.fao || it.patientName || "—"}</td>
                        <td style={S.td}>{fmtDate(g.key === "completedToday" ? it.completedAt : it.dueDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </>
      );
    }

    case "cdRegister": {
      const registers = data.registers || [];
      if (!registers.length) return <div style={S.muted}>No active registers.</div>;
      return (
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Register</th><th style={S.th}>Balance</th>
            <th style={S.th}>Last checked</th><th style={S.th}>Status</th>
          </tr></thead>
          <tbody>
            {registers.map((r, i) => {
              const bad = r.state === "overdue" || r.state === "never";
              const bg = bad ? "#fee2e2" : "transparent";
              const statusText = r.state === "overdue"
                ? `Overdue${r.daysOverdue != null ? ` ${r.daysOverdue}d` : ""}`
                : r.state === "never" ? "Never checked" : "OK";
              return (
                <tr key={i} style={{ background: bg, cursor: "pointer" }}
                    onClick={() => onRowDetail({
                      title: `CD register — ${r.registerName || "register"}`,
                      fields: [
                        ["Register", r.registerName], ["Category", r.category],
                        ["Current balance", r.currentBalance],
                        ["Last balance check", fmtDate(r.balanceCheckedAt)],
                        ["Status", statusText],
                        ["Expired entries", r.totalExpired]
                      ]
                    })}>
                  <td style={S.td}>{r.registerName || "—"}</td>
                  <td style={S.td}>{r.currentBalance ?? "—"}</td>
                  <td style={S.td}>{fmtDate(r.balanceCheckedAt)}</td>
                  <td style={S.td}>{statusText}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    case "patientSafety": {
      const incidents = data.incidents || [];
      if (!incidents.length) return <div style={S.muted}>No incidents recorded.</div>;
      return (
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Date</th><th style={S.th}>Type</th><th style={S.th}>LFPSE ref</th>
          </tr></thead>
          <tbody>
            {incidents.map((it, i) => {
              const incomplete = it.lfpseSubmitted && !it.lfpseReference;
              return (
                <tr key={i} style={{ background: incomplete ? "#fef3c7" : "transparent", cursor: "pointer" }}
                    onClick={() => onRowDetail({
                      title: "Patient safety incident",
                      fields: [
                        ["Date", fmtDate(it.incidentDate || it.createdAt)],
                        ["Type", it.incidentType || it.type],
                        ["Description", it.description],
                        ["LFPSE submitted", it.lfpseSubmitted ? "Yes" : "No"],
                        ["LFPSE reference", it.lfpseReference || (incomplete ? "Missing" : null)]
                      ]
                    })}>
                  <td style={S.td}>{fmtDate(it.incidentDate || it.createdAt)}</td>
                  <td style={S.td}>{it.incidentType || it.type || "—"}</td>
                  <td style={S.td}>{it.lfpseReference || (incomplete ? "Missing" : "—")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    case "accidentBook": {
      // riddorGrid returns { notYetKnown, reportable, notReportable }.
      const groups = [
        { key: "notYetKnown",  title: "RIDDOR status not yet known", bg: "#fee2e2" },
        { key: "reportable",   title: "RIDDOR reportable",           bg: "#fef3c7" },
        { key: "notReportable",title: "Not reportable",              bg: "transparent" }
      ];
      const any = groups.some(g => (data[g.key] || []).length);
      if (!any) return <div style={S.muted}>No accidents recorded.</div>;
      return (
        <>
          {groups.map(g => {
            const items = data[g.key] || [];
            if (!items.length) return null;
            return (
              <div key={g.key} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{g.title} ({items.length})</div>
                <table style={S.table}>
                  <thead><tr>
                    <th style={S.th}>Date</th><th style={S.th}>Manager</th><th style={S.th}>Detail</th>
                  </tr></thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i} style={{ background: g.bg, cursor: "pointer" }}
                          onClick={() => onRowDetail({
                            title: "Accident book entry",
                            fields: [
                              ["Date", fmtDate(it.accidentDate || it.date)],
                              ["Manager", it.managerName],
                              ["RIDDOR status", it.riddorStatus],
                              ["RIDDOR reference", it.reference],
                              ["Detail", it.description],
                              ["Days since", it.daysSinceAccident]
                            ]
                          })}>
                        <td style={S.td}>{fmtDate(it.accidentDate || it.date)}</td>
                        <td style={S.td}>{it.managerName || "—"}</td>
                        <td style={S.td}>{it.reference || it.description || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </>
      );
    }

    case "visitorLogs": {
      const visitors = data.visitors || [];
      if (!visitors.length) return <div style={S.muted}>No visitors recorded.</div>;
      return (
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Visitor</th><th style={S.th}>Company</th>
            <th style={S.th}>In</th><th style={S.th}>Out</th>
          </tr></thead>
          <tbody>
            {visitors.map((v, i) => {
              const stale = v.signedInAt && !v.signedOutAt;
              return (
                <tr key={i} style={{ background: stale ? "#fef3c7" : "transparent", cursor: "pointer" }}
                    onClick={() => onRowDetail({
                      title: "Visitor",
                      fields: [
                        ["Visitor", v.visitorName || v.name],
                        ["Company", v.company],
                        ["Reason", v.reason || v.purpose],
                        ["Signed in", fmtDateTime(v.signedInAt)],
                        ["Signed out", v.signedOutAt ? fmtDateTime(v.signedOutAt) : "Still on site"]
                      ]
                    })}>
                  <td style={S.td}>{v.visitorName || v.name || "—"}</td>
                  <td style={S.td}>{v.company || "—"}</td>
                  <td style={S.td}>{fmtDateTime(v.signedInAt)}</td>
                  <td style={S.td}>{v.signedOutAt ? fmtDateTime(v.signedOutAt) : "Still on site"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    default:
      return <div style={S.muted}>—</div>;
  }
}

// -----------------------------------------------------------------------------
// Activity card — clinic (services) or NMS, scoped to one pharmacy. Reuses the
// existing /services_analysis and /nms_analysis endpoints (POST {allowedPharmacies}),
// then filters the response to the selected pharmacy by name.
// -----------------------------------------------------------------------------
function ActivityCard({ label, endpoint, listKey, token, pharmacyName, kind }) {
  const [rows, setRows] = useState(null);
  const [raw, setRaw] = useState(null);      // this pharmacy's raw payload (for the chart)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showGraph, setShowGraph] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setRows(null); setRaw(null);

    const members = JSON.parse(localStorage.getItem("members") || "[]");
    const organisationName = localStorage.getItem("organisationName") || "";
    const allowedPharmacies = organisationName === "Pharmace all"
      ? ["xxx"]
      : members.map(m => m.pharmacyId);

    fetch(`${API_BASE}/${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ allowedPharmacies })
    })
      .then(r => r.json())
      .then(json => {
        if (cancelled) return;
        const data = json.data || [];
        const mine = data.find(p => p.pharmacyName === pharmacyName);
        setRaw(mine || null);
        setRows(summarise(mine, kind, listKey));
        setLoading(false);
      })
      .catch(e => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [endpoint, listKey, token, pharmacyName, kind]);

  const canGraph = kind === "services" && raw && (raw.services || []).length > 0;

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={S.cardLabel}>{label}</div>
        {canGraph && (
          <button onClick={() => setShowGraph(true)} style={S.linkBtn}>View graph →</button>
        )}
      </div>
      {loading ? (
        <div style={S.muted}>Loading…</div>
      ) : error ? (
        <div style={{ color: "#7f1d1d", fontSize: 13 }}>Couldn’t load: {error}</div>
      ) : !rows || rows.length === 0 ? (
        <div style={S.muted}>No activity recorded.</div>
      ) : (
        <table style={{ ...S.table, cursor: canGraph ? "pointer" : "default" }}
               onClick={canGraph ? () => setShowGraph(true) : undefined}>
          <thead><tr>
            <th style={S.th}>{kind === "nms" ? "Stage" : "Service"}</th>
            <th style={S.th}>Completed</th>
            <th style={S.th}>{kind === "nms" ? "In progress" : "Pending"}</th>
            {kind === "services" && <th style={S.th}>Cancelled</th>}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={S.td}>{r.name}</td>
                <td style={S.td}>{r.completed}</td>
                <td style={S.td}>{r.middle}</td>
                {kind === "services" && <td style={S.td}>{r.cancelled}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showGraph && canGraph && (
        <ClinicGraphPopup pharm={raw} pharmacyName={pharmacyName} onClose={() => setShowGraph(false)} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Route activity mini-widget — recent delivery rounds for this pharmacy.
// Reuses /routeanalysis_list (POST {pharmacyIds:[dbName], page, limit}).
// Resolves the pharmacy's dbName from localStorage members by name (same source
// the full Route analysis page uses).
// -----------------------------------------------------------------------------
function RouteActivityCard({ token, pharmacyName, goTo }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setItems(null);

    const members = JSON.parse(localStorage.getItem("members") || "[]");
    const mem = members.find(m => m.pharmacyName === pharmacyName);
    const dbName = mem?.pharmacyId ? String(mem.pharmacyId) : null;
    if (!dbName) { setItems([]); setLoading(false); return () => {}; }

    fetch(`${API_BASE}/routeanalysis_list`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ pharmacyIds: [dbName], month: null, year: null, page: 1, limit: 10 })
    })
      .then(r => r.json())
      .then(json => {
        if (cancelled) return;
        setItems(Array.isArray(json?.items) ? json.items : []);
        setLoading(false);
      })
      .catch(e => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [token, pharmacyName]);

  // Clicking a round opens the FULL Route analysis page, pre-filtered to this
  // pharmacy (the page reloads its own detail panel, map and all).
  const openInRoutePage = () => {
    if (goTo) goTo("route_analysis", { pharmacyName });
  };

  // Per-round helpers (mirror the main Route page).
  const succ = (it) => Number(it?.results?.deliveries?.successCount || 0);
  const fail = (it) => Number(it?.results?.deliveries?.failCount || 0);
  const deliveries = (it) => succ(it) + fail(it);
  const seconds = (it) => Number(it?.results?.aggregates?.totalActualSecondsForRoute || 0);
  const distanceKm = (it) => {
    const a = it?.results?.aggregates || {};
    const v = a.totalPredictedDistanceKm ?? a.totalOsrmDistanceKm ?? a.totalActualDistanceKm;
    return Number(v || 0) / 1000;   // values are stored in metres
  };
  const secondsToHM = (s) => {
    s = Math.max(0, Math.floor(Number(s) || 0));
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
    return h ? `${h}h ${m}m` : `${m}m`;
  };

  // Totals across the listed rounds.
  const totals = (items || []).reduce((acc, it) => {
    acc.deliveries += deliveries(it);
    acc.failed += fail(it);
    acc.seconds += seconds(it);
    acc.km += distanceKm(it);
    return acc;
  }, { deliveries: 0, failed: 0, seconds: 0, km: 0 });

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={S.cardLabel}>Route activity</div>
        {items && items.length > 0 && goTo && (
          <button onClick={openInRoutePage} style={S.linkBtn}>Open full route analysis →</button>
        )}
      </div>
      {loading ? (
        <div style={S.muted}>Loading…</div>
      ) : error ? (
        <div style={{ color: "#7f1d1d", fontSize: 13 }}>Couldn’t load: {error}</div>
      ) : !items || items.length === 0 ? (
        <div style={S.muted}>No recent delivery rounds.</div>
      ) : (
        <>
          {/* Totals across the recent rounds shown below */}
          <div style={S.statRow}>
            <Stat label="Total deliveries" value={totals.deliveries} />
            <Stat label="Failed deliveries" value={totals.failed} highlight={totals.failed > 0} />
            <Stat label="Total time" value={secondsToHM(totals.seconds)} />
            <Stat label="Total distance" value={`${totals.km ? totals.km.toFixed(1) : 0} km`} />
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 8 }}>
            Totals cover the {items.length} most recent round{items.length === 1 ? "" : "s"} shown.
          </div>

          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Date</th><th style={S.th}>Driver</th>
              <th style={S.th}>Deliveries</th><th style={S.th}>Failed</th>
              <th style={S.th}>Time</th><th style={S.th}>Distance</th><th style={S.th}>Efficiency</th>
            </tr></thead>
            <tbody>
              {items.map((it, i) => {
                const eff = it?.results?.aggregates?.efficiencyActualVsPredicted;
                const km = distanceKm(it);
                return (
                  <tr key={i} style={{ cursor: "pointer" }}
                      title="Open the full route detail in Route analysis"
                      onClick={openInRoutePage}>
                    <td style={S.td}>{fmtDate(it.deliveryDate)}</td>
                    <td style={S.td}>{it.driverName || "—"}</td>
                    <td style={S.td}>{deliveries(it)}</td>
                    <td style={{ ...S.td, color: fail(it) > 0 ? "#b91c1c" : "inherit" }}>{fail(it)}</td>
                    <td style={S.td}>{seconds(it) ? secondsToHM(seconds(it)) : "—"}</td>
                    <td style={S.td}>{km ? `${km.toFixed(1)} km` : "—"}</td>
                    <td style={S.td}>{eff != null ? eff : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// Small stat tile for the route totals row.
function Stat({ label, value, highlight }) {
  return (
    <div style={S.stat}>
      <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: highlight ? "#b91c1c" : "#111827" }}>{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Clinic graph popup — mirrors the Services page chart, locked to one pharmacy.
// Service-selectable + group-by selector + stacked Completed/Cancelled bars.
// -----------------------------------------------------------------------------
function ClinicGraphPopup({ pharm, pharmacyName, onClose }) {
  const [selectedService, setSelectedService] = useState("ALL");
  const [groupMode, setGroupMode] = useState("week");

  const services = useMemo(() => {
    const set = new Set();
    (pharm?.services || []).forEach(s => { if (s.serviceName) set.add(s.serviceName); });
    return Array.from(set).sort();
  }, [pharm]);

  // Build {date -> {completed, cancelled}} for the selected service/group.
  const graphData = useMemo(() => {
    const filtered = [];
    (pharm?.services || []).forEach(s => {
      if (selectedService !== "ALL" &&
          s.serviceName.toLowerCase().trim() !== selectedService.toLowerCase().trim()) return;
      (s.dailyStats || []).forEach(d => filtered.push({ date: d.date, stats: d.stats }));
    });

    const getKey = (dateStr) => {
      if (!dateStr) return "—";
      const [d, m, y] = String(dateStr).split("/");
      const dt = new Date(`${y}-${m}-${d}`);
      if (groupMode === "day") return dateStr;
      if (groupMode === "week") return `${m}/${y} W${Math.ceil((dt.getDate() || 1) / 7)}`;
      if (groupMode === "month") return `${m}/${y}`;
      if (groupMode === "year") return `${y}`;
      return dateStr;
    };

    const grouped = {};
    filtered.forEach(item => {
      const key = getKey(item.date);
      if (!grouped[key]) grouped[key] = { completed: 0, cancelled: 0 };
      (item.stats || []).forEach(st => {
        if (st.status === "COMPLETED") grouped[key].completed += st.count || 0;
        if (st.status === "CANCELLED") grouped[key].cancelled += st.count || 0;
      });
    });
    return Object.entries(grouped).map(([date, v]) => ({ date, ...v }));
  }, [pharm, selectedService, groupMode]);

  const xAxis = graphData.map(d => d.date);
  const chartOption = {
    tooltip: { trigger: "axis" },
    legend: { top: 0, data: ["Completed", "Cancelled"] },
    grid: { top: 50, left: 40, right: 20, bottom: 60 },
    xAxis: { type: "category", data: xAxis, axisLabel: { rotate: 45 } },
    yAxis: { type: "value" },
    series: [
      { name: "Completed", type: "bar", stack: "total", data: graphData.map(d => d.completed), itemStyle: { color: "green" } },
      { name: "Cancelled", type: "bar", stack: "total", data: graphData.map(d => d.cancelled), itemStyle: { color: "red" } }
    ]
  };

  return (
    <div className="popup" onClick={onClose}>
      <div className="popup-content" onClick={e => e.stopPropagation()} style={{ width: "min(900px, 95vw)" }}>
        <button className="close-btn" onClick={onClose}>X</button>
        <h3 style={{ marginTop: 0 }}>Clinic activity — {pharmacyName}</h3>

        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <select value={selectedService} onChange={e => setSelectedService(e.target.value)}
                  style={S.select}>
            <option value="ALL">All services</option>
            {services.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={groupMode} onChange={e => setGroupMode(e.target.value)} style={S.select}>
            <option value="day">By day</option>
            <option value="week">By week</option>
            <option value="month">By month</option>
            <option value="year">By year</option>
          </select>
        </div>

        {graphData.length === 0 ? (
          <div style={S.muted}>No data for this selection.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div style={{ width: Math.max(700, xAxis.length * 70), height: 380 }}>
              <ReactECharts
                key={`${selectedService}-${groupMode}`}
                option={chartOption}
                style={{ height: "100%", width: "100%" }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Reduce one pharmacy's services/nms payload into compact per-item rows.
function summarise(pharm, kind, listKey) {
  if (!pharm) return [];
  const list = pharm[listKey] || [];

  if (kind === "services") {
    return list.map(s => {
      let completed = 0, cancelled = 0;
      (s.dailyStats || []).forEach(d => (d.stats || []).forEach(st => {
        if (st.status === "COMPLETED") completed += st.count || 0;
        if (st.status === "CANCELLED") cancelled += st.count || 0;
      }));
      return { name: s.serviceName || "—", completed, middle: s.pendingCount || 0, cancelled };
    }).filter(r => r.completed || r.middle || r.cancelled);
  }

  // NMS: group by type across breakdown counts, keyed by service/stage name.
  const byName = {};
  list.forEach(group => {
    const name = group.serviceName || group.name || group.type || "NMS";
    if (!byName[name]) byName[name] = { name, completed: 0, middle: 0 };
    (group.breakdown || []).forEach(b => {
      if (group.type === "COMPLETED") byName[name].completed += b.count || 0;
      if (group.type === "IN_PROGRESS") byName[name].middle += b.count || 0;
    });
  });
  return Object.values(byName).filter(r => r.completed || r.middle);
}

// -----------------------------------------------------------------------------
// Periodic tasks — scoped to ONE pharmacy, using the group-wide Periodic Tasks
// system (periodic_task_defs / entries). Rewired 2026-06 off the legacy
// periodic-entries collection. Lists every active task definition, shows this
// pharmacy's latest recorded value + due-state, and lets staff record a new
// value per task.
//
// Backend:
//   GET  /api/headoffice/periodic-tasks/grid   → { tasks[], rows[] }
//        each row = { pharmacyId, pharmacyName, dbName, cells{ taskId: {value, recordedAt, state} } }
//   POST /api/headoffice/periodic-tasks/entries  { taskId, dbName, pharmacyId, value }
//   POST /api/headoffice/periodic-tasks/optout   { taskId, dbName, pharmacyId, optedOut }
// -----------------------------------------------------------------------------

// Due-state → colour + label. States from the backend grid: in-date, due-soon,
// overdue, never, na (opted out).
const PT_STATE = {
  "in-date":  { bg: "#dcfce7", fg: "#166534", label: "In date" },
  "due-soon": { bg: "#fef9c3", fg: "#854d0e", label: "Due soon" },
  "overdue":  { bg: "#fee2e2", fg: "#991b1b", label: "Overdue" },
  "never":    { bg: "#f3f4f6", fg: "#6b7280", label: "Never recorded" },
  "na":       { bg: "#eef2ff", fg: "#3730a3", label: "Not relevant" },
};

function ptDisplayValue(dataType, value) {
  if (value == null || value === "") return "—";
  if (dataType === "boolean") return (value === true || value === "true" || value === "yes" || value === 1) ? "Yes" : "No";
  if (dataType === "date") { const d = new Date(value); return isNaN(d) ? String(value) : d.toLocaleDateString("en-GB"); }
  return String(value);
}

function PeriodicTasksCard({ pharmacy, token }) {
  const [tasks, setTasks] = useState([]);
  const [row, setRow] = useState(null);       // this pharmacy's grid row
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const g = await apiGet("periodic-tasks/grid", token);
      setTasks(g?.tasks || []);
      const mine = (g?.rows || []).find(
        r => String(r.pharmacyId) === String(pharmacy.pharmacyId)
      ) || null;
      setRow(mine);
    } catch (e) { setError(String(e.message || e)); }
    finally { setLoading(false); }
  }, [pharmacy.pharmacyId, token]);

  useEffect(() => { load(); }, [load]);

  // Record a value for one task against this pharmacy.
  const recordValue = async (task, value, periodKey) => {
    if (!row?.dbName) { window.alert("Can't record — this pharmacy has no database name in the fleet grid."); return; }
    setSavingId(task.taskId);
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/periodic-tasks/entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          taskId: task.taskId,
          dbName: row.dbName,
          pharmacyId: pharmacy.pharmacyId,
          value,
          periodKey: periodKey || undefined,   // 'YYYY-MM' the value applies to
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) { window.alert("Couldn't record: " + (e.message || e)); }
    finally { setSavingId(null); }
  };

  // Toggle "not relevant" for one task at this pharmacy.
  const toggleOptout = async (task, optedOut) => {
    if (!row?.dbName) return;
    try {
      await fetch(`${API_BASE}/api/headoffice/periodic-tasks/optout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ taskId: task.taskId, dbName: row.dbName, pharmacyId: pharmacy.pharmacyId, optedOut }),
      });
      await load();
    } catch (e) { window.alert("Couldn't update: " + (e.message || e)); }
  };

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={S.cardLabel}>Periodic tasks</div>
        <button onClick={load} style={S.navBtn}>Refresh</button>
      </div>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
        Group-defined recurring tasks. Showing this pharmacy's latest value and status for each.
      </div>

      {loading ? (
        <div style={S.muted}>Loading…</div>
      ) : error ? (
        <div style={{ color: "#7f1d1d", fontSize: 13 }}>Couldn’t load: {error}</div>
      ) : tasks.length === 0 ? (
        <div style={S.muted}>No periodic tasks defined for this group yet.</div>
      ) : (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Task</th>
              <th style={S.th}>Frequency</th>
              <th style={S.th}>Latest value</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Record</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map(task => {
              const cell = row?.cells?.[String(task.taskId)] || { state: "never", value: null };
              const st = PT_STATE[cell.state] || PT_STATE.never;
              const isNa = cell.state === "na";
              return (
                <tr key={String(task.taskId)}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 600 }}>{task.description}</div>
                    {cell.recordedAt && (
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        Last: {new Date(cell.recordedAt).toLocaleDateString("en-GB")}
                      </div>
                    )}
                  </td>
                  <td style={{ ...S.td, textTransform: "capitalize" }}>{task.frequency}</td>
                  <td style={S.td}>{isNa ? "—" : ptDisplayValue(task.dataType, cell.value)}</td>
                  <td style={S.td}>
                    <span style={{ background: st.bg, color: st.fg, fontSize: 11, fontWeight: 700,
                                   padding: "2px 8px", borderRadius: 999 }}>{st.label}</span>
                  </td>
                  <td style={S.td}>
                    {isNa ? (
                      <button onClick={() => toggleOptout(task, false)} style={{ ...S.linkBtn, padding: "2px 8px" }}>
                        Mark relevant
                      </button>
                    ) : (
                      <PeriodicTaskRecorder
                        task={task}
                        saving={savingId === task.taskId}
                        onRecord={(v, pk) => recordValue(task, v, pk)}
                        onOptout={() => toggleOptout(task, true)}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Inline recorder for one task — input shape depends on the task's dataType.
function PeriodicTaskRecorder({ task, saving, onRecord, onOptout }) {
  const [val, setVal] = useState("");
  // Which period the value applies to (monthly tasks). Defaults to the
  // current month; last 13 months offered so figures can be back-entered.
  const monthOpts = (() => {
    const out = []; const now = new Date();
    let y = now.getUTCFullYear(), m = now.getUTCMonth() + 1;
    for (let i = 0; i < 13; i++) {
      out.push(`${y}-${String(m).padStart(2, "0")}`);
      m--; if (m < 1) { m = 12; y--; }
    }
    return out;
  })();
  const [period, setPeriod] = useState(monthOpts[0]);
  const isMonthly = task.frequency === "monthly";
  const pk = isMonthly ? period : undefined;

  const monthLabel = (k) => {
    const [y, m] = k.split("-").map(Number);
    return `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][m - 1]} ${String(y).slice(2)}`;
  };
  const PeriodSelect = () => !isMonthly ? null : (
    <select value={period} onChange={e => setPeriod(e.target.value)}
      title="Month this value applies to — lets you enter or correct figures for previous months."
      style={{ ...S.cellInput, width: "auto" }}>
      {monthOpts.map(k => <option key={k} value={k}>{monthLabel(k)}</option>)}
    </select>
  );

  const commit = () => {
    if (task.dataType === "boolean") return; // handled by Yes/No buttons
    if (val === "") return;
    onRecord(val, pk);
    setVal("");
  };

  if (task.dataType === "boolean") {
    return (
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <PeriodSelect />
        <button disabled={saving} onClick={() => onRecord(true, pk)} style={{ ...S.linkBtn, padding: "2px 10px" }}>Yes</button>
        <button disabled={saving} onClick={() => onRecord(false, pk)} style={{ ...S.linkBtn, padding: "2px 10px", color: "#b91c1c", borderColor: "#fecaca" }}>No</button>
        <OptoutLink onOptout={onOptout} />
      </div>
    );
  }

  if (task.dataType === "choice") {
    return (
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <PeriodSelect />
        <select value={val} onChange={e => setVal(e.target.value)} style={{ ...S.cellInput, width: "auto" }}>
          <option value="">Select…</option>
          {(task.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <button disabled={saving || val === ""} onClick={commit} style={{ ...S.linkBtn, padding: "2px 8px" }}>Save</button>
        <OptoutLink onOptout={onOptout} />
      </div>
    );
  }

  // numeric / date / text
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      <PeriodSelect />
      <input
        type={task.dataType === "numeric" ? "number" : task.dataType === "date" ? "date" : "text"}
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") commit(); }}
        style={{ ...S.cellInput, width: task.dataType === "date" ? 140 : 90 }}
        placeholder={task.dataType === "numeric" ? "0" : ""}
      />
      <button disabled={saving || val === ""} onClick={commit} style={{ ...S.linkBtn, padding: "2px 8px" }}>Save</button>
      <OptoutLink onOptout={onOptout} />
    </div>
  );
}

function OptoutLink({ onOptout }) {
  return (
    <button
      onClick={onOptout}
      title="Mark this task as not relevant for this pharmacy"
      style={{ ...S.linkBtn, padding: "2px 6px", color: "#6b7280", borderColor: "#e5e7eb", fontSize: 11 }}
    >
      N/A
    </button>
  );
}

const S = {
  page: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 },
  selectorRow: { display: "flex", alignItems: "center", marginBottom: 20, padding: "12px 14px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6 },
  select: { fontSize: 14, padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 4, minWidth: 260 },
  card: { width: "100%", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "16px 18px", marginBottom: 16, boxSizing: "border-box" },
  cardLabel: { fontSize: 16, fontWeight: 600, marginBottom: 10 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e5e7eb", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: "#6b7280" },
  td: { padding: "6px 8px", borderBottom: "1px solid #f3f4f6" },
  muted: { fontSize: 13, color: "#9ca3af", fontStyle: "italic" },
  linkBtn: { fontSize: 12, padding: "4px 10px", border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", color: "#2D5A3D", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" },
  statRow: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 },
  stat: { flex: "1 1 120px", minWidth: 110, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px" },
  navBtn: { fontSize: 12, padding: "5px 12px", border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", color: "#374151", cursor: "pointer" },
  cellInput: { width: "100%", fontSize: 13, border: "1px solid #e5e7eb", borderRadius: 4, padding: "4px 6px", boxSizing: "border-box" }
};